import React, { forwardRef, useRef } from 'react';

interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  /** Shown as a hover-only "ⓘ" next to the label — for a short explanation
   * that would be clutter if always visible (e.g. a formula), unlike
   * `helperText` which renders below the field permanently. */
  tooltip?: string;
  error?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'outlined' | 'filled';
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      tooltip,
      error,
      helperText,
      size = 'md',
      variant = 'default',
      fullWidth = false,
      startIcon,
      endIcon,
      className = '',
      style = {},
      required = false,
      ...props
    },
    ref
  ) => {
    const getSizeClasses = (): string => {
      switch (size) {
        case 'sm':
          return 'h-9 px-3 text-sm';
        case 'lg':
          return 'h-12 px-4 text-base';
        default:
          return 'h-10 px-3 text-sm sm:text-base';
      }
    };

    const getVariantStyles = (): React.CSSProperties => {
      switch (variant) {
        case 'outlined':
          return {
            backgroundColor: 'transparent',
            border: '2px solid var(--border)',
            color: 'var(--text-primary)',
            ...style,
          };
        case 'filled':
          return {
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid transparent',
            color: 'var(--text-primary)',
            ...style,
          };
        default:
          return {
            backgroundColor: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            ...style,
          };
      }
    };

    const inputClasses = [
      'input',
      getSizeClasses(),
      fullWidth ? 'w-full' : '',
      error ? 'border-red-500 focus:border-red-500' : 'focus:border-accent',
      startIcon ? 'pl-10' : '',
      endIcon ? 'pr-10' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const inputId =
      props.id || `input-${Math.random().toString(36).substr(2, 9)}`;


    const localInputRef = useRef<HTMLInputElement>(null);
    const combinedRef = (node: HTMLInputElement) => {
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
      localInputRef.current = node;
    };

    const openCalendar = () => {
      if (props.type === 'date' && localInputRef.current) {
        // Chrome only
        if (typeof localInputRef.current.showPicker === 'function') {
          localInputRef.current.showPicker();
        } else {
          localInputRef.current.click();
        }
      }
    };

    return (
props.type === 'checkbox' ? (
  <div className='mt-7'>
  <label
    htmlFor={inputId}
    className="flex items-center space-x-3 cursor-pointer select-none"
    style={{ color: 'var(--text-primary)' }}
  >
    <input
      id={inputId}
      ref={combinedRef}
      type="checkbox"
      className="sr-only"
      {...props}
    />
    <div
      className={`relative inline-block w-10 h-6 transition rounded-full ${
        localInputRef.current?.checked ? 'bg-[var(--accent)]' : 'bg-gray-300'
      }`}
    >        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              <span style={{ color: 'var(--text-secondary)' }}>
                {helperText}
              </span>
            )}
          </div>
        )}
      <span
        className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform transform ${
          localInputRef.current?.checked ? 'translate-x-4' : ''
        }`}
      />
    </div>
    {label && <span className="text-sm">{label}</span>}
  </label>
  </div>
) : 
(
      <div className={`flex flex-col ${fullWidth ? 'w-full' : ''}`}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-2 text-sm font-medium"
            style={{ color: 'var(--text-primary)' }}
          >
            {label} {required && <span className="text-red-500 ml-1">*</span>}
            {tooltip && (
              <span
                title={tooltip}
                className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-xs cursor-help select-none"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--text-secondary)' }}
              >
                i
              </span>
            )}
          </label>
        )}
        <div className="relative">
          {startIcon && (
            <div
              className="absolute left-3 top-1/2 transform -translate-y-1/2"
              style={{ color: 'var(--text-secondary)' }}
              onClick={openCalendar}
            >
              {startIcon}
            </div>
          )}
          <input
            ref={combinedRef}
            id={inputId}
            className={inputClasses}
            style={getVariantStyles()}
            onClick={props.type === 'date' ? openCalendar : undefined}
            {...props}
            onWheel={(e) => {
              props.onWheel?.(e);
              // Scrolling while a number input happens to be focused
              // silently changes its value (a mouse-wheel accident, not
              // deliberate use of the [removed] spinner buttons) — blur it
              // so the page scrolls normally instead.
              if (props.type === 'number') e.currentTarget.blur();
            }}
          />
          {endIcon && (
            <div
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              style={{ color: 'var(--text-secondary)' }}
            >
              {endIcon}
            </div>
          )}
        </div>
        {(error || helperText) && (
          <div className="mt-1 text-xs">
            {error ? (
              <span className="text-red-500">{error}</span>
            ) : (
              <span style={{ color: 'var(--text-secondary)' }}>
                {helperText}
              </span>
            )}
          </div>
        )}
      </div>
    )
    );
  }
);

Input.displayName = 'Input';

export default Input;
